// Mock data for OES (Originality Enabling System)

export const stageInfo = {
  sparkActivation: {
    title: 'Spark Activation',
    stageNumber: 1,
    description: 'Trigger novelty and generate initial ideas through divergent thinking.',
    color: 'from-blue-500 to-cyan-500',
    prompts: [
      "What if you could solve any problem without constraints? What would you explore?",
      "What if you combined two completely unrelated concepts? What emerges?",
      "What if the opposite of your initial approach was true? What would that reveal?",
      "What if you had unlimited resources and no limitations? What becomes possible?",
      "What if you could reimagine this from scratch? What would you create?"
    ]
  },
  mirrorValidation: {
    title: 'Mirror Validation',
    stageNumber: 2,
    description: 'Check your idea against existing innovations and prior art.',
    color: 'from-violet-500 to-purple-500',
    prompts: [
      "If you had to describe your core idea in one sentence, what would it be?",
      "What if similar solutions already exist? How is yours different?",
      "What if you examined existing innovations closely? What makes your approach unique?",
      "If someone else had this idea, where would you have seen it?",
      "What if you combined existing elements in a new way? What's your unique angle?"
    ]
  },
  oiScoring: {
    title: 'Originality Index',
    stageNumber: 3,
    description: 'Score your idea across four dimensions of originality.',
    color: 'from-orange-500 to-red-500',
    dimensions: [
      {
        name: 'Novelty',
        weight: 0.3,
        description: 'Semantic distance, rarity of combination, originality of form, disruptive potential',
        scale: [
          { range: '0-2', label: 'Derivative/Clone' },
          { range: '3-4', label: 'Incremental' },
          { range: '5-6', label: 'Fresh Variation' },
          { range: '7-8', label: 'Strongly Original' },
          { range: '9-10', label: 'Breakthrough' }
        ]
      },
      {
        name: 'Resonance',
        weight: 0.3,
        description: 'Emotional engagement, cultural relevance, accessibility, virality',
        scale: [
          { range: '0-2', label: 'Ignored' },
          { range: '3-4', label: 'Niche Appeal' },
          { range: '5-6', label: 'Moderate Interest' },
          { range: '7-8', label: 'Strong Connection' },
          { range: '9-10', label: 'Cultural Shift' }
        ]
      },
      {
        name: 'Synthesis',
        weight: 0.2,
        description: 'Cross-disciplinary integration, cross-cultural integration, depth, generativity',
        scale: [
          { range: '0-2', label: 'Fragmented' },
          { range: '3-4', label: 'Weak Links' },
          { range: '5-6', label: 'Moderate Integration' },
          { range: '7-8', label: 'Strong Synthesis' },
          { range: '9-10', label: 'Elegant Fusion' }
        ]
      },
      {
        name: 'Ethical Anchoring',
        weight: 0.2,
        description: 'Attribution, inclusivity, fairness, cultural respect',
        scale: [
          { range: '0-2', label: 'Harmful' },
          { range: '3-4', label: 'Questionable' },
          { range: '5-6', label: 'Neutral' },
          { range: '7-8', label: 'Responsible' },
          { range: '9-10', label: 'Deeply Ethical' }
        ]
      }
    ]
  },
  msaMapping: {
    title: 'Mirror-Spark Assessment',
    stageNumber: 4,
    description: 'Distinguish human creativity from AI mimicry.',
    color: 'from-green-500 to-emerald-500',
    mirrorIndicators: [
      { name: 'Replication', max: 3, description: 'Degree of copying or reproduction of existing ideas' },
      { name: 'Trend Alignment', max: 2, description: 'Following current patterns or trends' },
      { name: 'Conformity', max: 3, description: 'Adherence to established norms' },
      { name: 'Surface Innovation', max: 2, description: 'Shallow novelty without depth' }
    ],
    sparkIndicators: [
      { name: 'Novel Insight', max: 3, description: 'Genuinely new perspective or understanding' },
      { name: 'Intuitive Leap', max: 2, description: 'Going beyond logical steps' },
      { name: 'Cultural Resonance', max: 3, description: 'Deep cultural connection and meaning' },
      { name: 'Generativity', max: 2, description: 'Ability to spawn further ideas' }
    ]
  },
  iqiSafeguard: {
    title: 'Interaction Quality Index',
    stageNumber: 5,
    description: 'Assess the quality of your creative process.',
    color: 'from-indigo-500 to-violet-500',
    dimensions: [
      {
        name: 'Tone',
        description: 'Was your exploration respectful, open, and constructive?',
        scale: ['Defensive/Closed', 'Somewhat Open', 'Balanced', 'Very Open', 'Fully Open & Curious']
      },
      {
        name: 'Depth',
        description: 'Did you go beyond surface-level exploration?',
        scale: ['Superficial', 'Basic', 'Moderate Depth', 'Deep', 'Profound']
      },
      {
        name: 'Compassion',
        description: 'Did you consider stakeholder needs with empathy?',
        scale: ['Self-centered', 'Limited Consideration', 'Balanced', 'Empathetic', 'Deeply Compassionate']
      }
    ]
  },
  iteration: {
    title: 'Iteration Cycle',
    stageNumber: 6,
    description: 'Refine your idea based on feedback and scores.',
    color: 'from-pink-500 to-rose-500',
    prompts: [
      "What if you could strengthen one aspect of your idea? Which would have the most impact?",
      "What if you made it 10x more original? What would change?",
      "What if your target audience could shape this idea? What would resonate deeper?",
      "What if you integrated insights from completely different fields? What connections emerge?",
      "What if you addressed ethical considerations more thoroughly? What would improve?",
      "If this idea were ready for the world, what's the final piece missing?"
    ]
  }
};

export const mockSessions = [
  {
    id: '1',
    title: 'Sustainable Urban Mobility Solution',
    createdAt: '2025-07-20T10:30:00Z',
    currentStage: 6,
    scores: {
      oi: 7.8,
      oiInterpretation: 'Moderate',
      mirrorPct: 35,
      sparkPct: 65,
      msaInterpretation: 'Strong Spark'
    },
    completed: true
  },
  {
    id: '2',
    title: 'AI-Powered Learning Platform',
    createdAt: '2025-07-18T14:20:00Z',
    currentStage: 4,
    scores: {
      oi: 6.2,
      oiInterpretation: 'Moderate',
      mirrorPct: 55,
      sparkPct: 45,
      msaInterpretation: 'Hybrid Originality'
    },
    completed: false
  }
];

export const getStageKeys = () => [
  'sparkActivation',
  'mirrorValidation',
  'oiScoring',
  'msaMapping',
  'iqiSafeguard',
  'iteration'
];